// Finding a story on /news (#3931, founder pick A of the 2026-10-11 mocks):
// the search and topic filter, the month groups, the guide a no-results
// search points to, and the signed-in "On your list" match. Pure functions so
// the page stays a renderer and the rules are tested on their own.
import { NEWS_TOPICS, topicById } from '../data/newsTopics.js';
import { storySchoolIds, kbId } from './astraMovesData.js';
import { gradeNumber } from './gradeYear.js';
import { US_STATE_NAMES } from './regions.js';

export const PAGE_SIZE = 20;

const eventOf = (s) => s.eventDate || s.publishedAt || '';
const norm = (q) => String(q || '').toLowerCase().replace(/\s+/g, ' ').trim();

const escapeRe = (w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Every typed word must START a word in the story's indexed words (title,
// summary, source, topic, type, school names, state), so "rice tuition"
// narrows to Rice and "rice" never finds "price".
export function filterStories(stories, index, { q = '', topic = '' } = {}) {
    const words = norm(q).split(' ').filter(Boolean).map((w) => new RegExp(`(?:^|[^a-z0-9])${escapeRe(w)}`));
    return stories.filter((s) => {
        if (topic && s.topic !== topic) return false;
        if (!words.length) return true;
        const text = index?.[s.slug]?.text || norm(`${s.title} ${s.listDek} ${s.sourceLine}`);
        return words.every((re) => re.test(text));
    });
}

// Chip counts over the stories a search leaves (the counts add up to the
// total because a story has one topic). A topic with no story renders no chip.
// The chip that is on stays, at 0, so a search never hides what is pressed.
export function topicCounts(stories, active = '') {
    const counts = {};
    for (const s of stories) counts[s.topic] = (counts[s.topic] || 0) + 1;
    return NEWS_TOPICS.filter((t) => counts[t.id] || t.id === active).map((t) => ({ ...t, count: counts[t.id] || 0 }));
}

// Newest month first; within a month the registry's own order (newest event).
export function groupByMonth(stories) {
    const groups = [];
    for (const s of stories) {
        const key = eventOf(s).slice(0, 7);
        const last = groups[groups.length - 1];
        if (last && last.key === key) last.stories.push(s);
        else groups.push({ key, stories: [s] });
    }
    return groups;
}

// ---- "More news" on a story page (#5009) ----

// How far either side of a story its "More news" reaches on the timeline.
// /news paints only PAGE_SIZE stories in its raw HTML and pages the rest
// behind "Show older" (a button, not a link), so every story published
// pushes the oldest page-one story out of the index's links. On 2026-10-21
// the approved Georgetown story pushed virginia-sat-school-day to a single
// inbound link and the publish gate (prerender.test.js, #2698) refused it.
// Two either side means every story is linked from at least two other story
// pages once there are three stories, whatever /news shows; do not drop it to
// one (the oldest story would then have one link, from the story above it).
export const MORE_NEWS_REACH = 2;

// The stories either side of `slug` in the registry's order (newest event
// first), newer ones first; empty for a slug not in the list.
export function timelineNeighbors(stories, slug, reach = MORE_NEWS_REACH) {
    const i = stories.findIndex((s) => s.slug === slug);
    if (i < 0) return [];
    return [...stories.slice(Math.max(0, i - reach), i), ...stories.slice(i + 1, i + 1 + reach)];
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const monthLabel = (key) => {
    const [y, m] = String(key).split('-');
    return MONTHS[Number(m) - 1] ? `${MONTHS[Number(m) - 1]} ${y}` : key;
};

// The evergreen guide a search with no results points to: the topic whose
// phrases the query names (the longest phrase wins, so "federal aid" beats
// "aid"), else the chip that is on. Null means "all guides".
export function guideTopicFor(q, activeTopic) {
    const text = ` ${norm(q).replace(/[^a-z0-9& -]/g, ' ')} `;
    let best = null;
    for (const t of NEWS_TOPICS) {
        for (const w of t.words) {
            if (text.includes(` ${w} `) && (!best || w.length > best.len) && t.guide) best = { topic: t, len: w.length };
        }
    }
    if (best) return best.topic;
    const active = topicById(activeTopic);
    return active?.guide ? active : null;
}

// ---- "On your list" (signed in) ----

// The graduating class, the server's class_year (astra_moves.py) in JS: the
// profile's graduation_year, else its grade counted from this cycle's seniors
// (from August a year's seniors are next June's graduates). Null when neither
// says, and an unknown class never reads as "your year".
export function classYear(profile, today = new Date()) {
    const p = profile || {};
    const gy = parseInt(String(p.graduation_year ?? '').trim().slice(0, 4), 10);
    if (gy > 2000 && gy < 2100) return gy;
    const g = gradeNumber(p.grade);
    if (g === null) return null;
    const seniors = today.getMonth() + 1 >= 8 ? today.getFullYear() + 1 : today.getFullYear();
    return seniors + (12 - g);
}

// The class half of the server's _story_applies, pinned to it by the shared
// fixture tests/fixtures/news_class_applies.json. Null when the story names
// no class, or the student's class is unknown.
export function classApplies(moves, cls) {
    const m = moves || {};
    const rules = [['fromClass', (c, v) => c >= v], ['toClass', (c, v) => c <= v], ['onlyClass', (c, v) => c === v]];
    const named = rules.filter(([k]) => m[k] != null);
    if (!named.length || cls == null) return null;
    return named.every(([k, test]) => test(cls, Number(m[k])));
}

// The grade-fit line for a story aimed at named classes: who it is for and
// whether that is this student, from the class fields, never the text. The
// twin of astra_moves.class_fit_line, so the box and Astra Today's news move
// say the same words (tests/fixtures/news_class_applies.json `lines`).
export function classFitLine(moves, cls) {
    const m = moves || {};
    let who = null;
    if (m.onlyClass != null) who = `For the class of ${m.onlyClass}`;
    else if (m.fromClass != null) who = `Starts with the class of ${m.fromClass}`;
    else if (m.toClass != null) who = `For the class of ${m.toClass} and earlier`;   // never "through" (#3955)
    if (!who) return null;
    const ok = classApplies(m, cls);
    return ok === null ? `${who}.` : ok ? `${who}, which includes you.` : `${who}. Not your year.`;
}

// The plain line under a story in the box: the grade-fit line, then the state
// a state's-students story is for. Never a guess about the student's state.
export function forWhomLine(story, cls) {
    const m = story.moves || {};
    const ok = classApplies(m, cls);
    const parts = [classFitLine(m, cls)];
    if (m.state && US_STATE_NAMES[m.state]) parts.push(`For ${US_STATE_NAMES[m.state]} students.`);
    return { text: parts.filter(Boolean).join(' ') || null, yours: ok === true, notYours: ok === false };
}

// The stories that name a school on the student's list, matched by KB id the
// way Astra Today's news move matches (storySchoolIds against the list rows'
// university_id), newest first. Returns the hit and the listed ids it named.
export function storiesOnList(stories, collegeList) {
    const listed = new Set((collegeList || []).map((r) => kbId(r?.university_id)).filter(Boolean));
    if (!listed.size) return [];
    return stories
        .map((s) => ({ story: s, ids: storySchoolIds(s).filter((id) => listed.has(id)) }))
        .filter((x) => x.ids.length);
}
